import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@common-fe/common-fe';
import { toString } from 'lodash';
import _ from 'lodash';

import { api } from '@/api';
import {
  API_URL,
  IClaimDtoClaimStatusEnum,
  ILetterOfMedicalNecessityCreateDto,
  ILetterOfMedicalNecessityDto,
} from '@/dtos';

import { LMNClaim } from '../LettersOfMedicalNecessity.types';

interface FormattedResponse {
  id?: string;
  claims?: LMNClaim[];
}

export interface CreateLMNPayload {
  name?: string;
  description?: string;
  documentId?: string;
  employeeId?: string;
  claimIds?: string[];
  validFrom?: string;
  validTo?: string;
  fileReassignment?: {
    fileId: string;
    pages?: number[];
  };
}

interface Params {
  onSuccess?: (id?: string) => void;
}
const useCreateLettersOfMedicalNecessityQuery = (params?: Params) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    (payload: CreateLMNPayload) => {
      const formattedPayload: ILetterOfMedicalNecessityCreateDto = {
        name: payload?.name || '',
        description: payload?.description || undefined,
        document_id: payload?.fileReassignment ? undefined : _.toNumber(payload.documentId || ''),
        valid_from: payload.validFrom as unknown as Date,
        valid_to: payload.validTo as unknown as Date,
        employee_id: _.toNumber(payload?.employeeId),
        claim_ids: payload?.claimIds?.length ? payload?.claimIds.map((item) => _.toNumber(item)) : [],
        ...(payload?.fileReassignment
          ? {
              file_reassignment: {
                file_id: payload.fileReassignment.fileId ? _.toNumber(payload.fileReassignment.fileId) : 0,
                pages: payload.fileReassignment.pages as unknown as Set<number>,
              },
            }
          : {}),
      };
      return api.post<ILetterOfMedicalNecessityDto>(API_URL.LETTERS_OF_MEDICAL_NECESSITY, formattedPayload);
    },

    {
      onSuccess: (res) => {
        if (params?.onSuccess) {
          params?.onSuccess(`${res?.data?.id || ''}`);
        }
        queryClient.invalidateQueries({
          queryKey: [API_URL.LETTERS_OF_MEDICAL_NECESSITY],
        });
      },
    }
  );

  const createLMN = useCallback(
    async (payload: CreateLMNPayload): Promise<FormattedResponse> => {
      const response = await mutateAsync(payload);
      return {
        id: toString(response?.data?.id),
        claims:
          response?.data?.claims?.map((claim) => ({
            claimId: toString(claim?.claim_id),
            providerName: claim?.provider_name,
            serviceId: toString(claim?.service_id),
            statusType: claim?.status_type as unknown as IClaimDtoClaimStatusEnum,
            approvalStatus: claim?.approval_status,
            requestedAmount: claim?.requested_amount,
            serviceOn: claim?.service_on as unknown as string,
          })) || [],
      };
    },
    [mutateAsync]
  );

  return {
    createLMN,
    isLoading,
  };
};

export default useCreateLettersOfMedicalNecessityQuery;
